import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/common/ProtectedRoute';
import PageLoader from '../components/common/PageLoader';
import Layout from '../components/layout/Layout';
import AdminLayout from '../components/admin/AdminLayout';
import SellerLayout from '../components/seller/SellerLayout';
import HeroBannerSlider from '../components/common/HeroBannerSlider';
import HomeBannerSlider from '../components/common/HomeBannerSlider';
import { ROUTES } from '../constants';

const DashboardPage      = lazy(() => import('../pages/DashboardPage'));
const HelpCenterPage     = lazy(() => import('../pages/HelpCenterPage'));
const FAQPage            = lazy(() => import('../pages/FAQPage'));
const FAQDetailPage      = lazy(() => import('../pages/FAQDetailPage'));
const ProfilePage        = lazy(() => import('../pages/ProfilePage'));
const UsersPage          = lazy(() => import('../pages/UsersPage'));
const HomePage           = lazy(() => import('../pages/HomePage'));
const ProductsPage       = lazy(() => import('../pages/ProductsPage'));
const ProductDetailPage  = lazy(() => import('../pages/ProductDetailPage'));
const NotFoundPage       = lazy(() => import('../pages/NotFoundPage'));
const CartPage           = lazy(() => import('../pages/CartPage'));
const CheckoutPage       = lazy(() => import('../pages/CheckoutPage'));
const OrderSuccessPage   = lazy(() => import('../pages/OrderSuccessPage'));
const OrdersPage         = lazy(() => import('../pages/OrdersPage'));
const OrderDetailPage    = lazy(() => import('../pages/OrderDetailPage'));
const SellerDashboardPage  = lazy(() => import('../pages/seller/SellerDashboardPage'));
const SellerProductsPage   = lazy(() => import('../pages/seller/SellerProductsPage'));
const SellerOrdersPage     = lazy(() => import('../pages/seller/SellerOrdersPage'));
const SellerStorePage      = lazy(() => import('../pages/seller/SellerStorePage'));
const SellerEarningsPage   = lazy(() => import('../pages/seller/SellerEarningsPage'));
const StoreProfilePage     = lazy(() => import('../pages/StoreProfilePage'));
const AdminDashboardPage   = lazy(() => import('../pages/admin/AdminDashboardPage'));
const AdminUsersPage       = lazy(() => import('../pages/admin/AdminUsersPage'));
const AdminOrdersPage      = lazy(() => import('../pages/admin/AdminOrdersPage'));
const AdminProductsPage    = lazy(() => import('../pages/admin/AdminProductsPage'));
const AdminCategoriesPage  = lazy(() => import('../pages/admin/AdminCategoriesPage'));
const AdminSellerApplicationsPage = lazy(() => import('../pages/admin/AdminSellerApplicationsPage'));
const SellerApplicationDetailPage = lazy(() => import('../pages/admin/SellerApplicationDetailPage'));
const AdminCouponsPage     = lazy(() => import('../pages/admin/AdminCouponsPage'));
const BecomeSellerPage     = lazy(() => import('../pages/BecomeSellerPage'));
const SellerHelpPage       = lazy(() => import('../pages/SellerHelpPage'));
const SellerGuidePage      = lazy(() => import('../pages/SellerGuidePage'));
const WishlistPage         = lazy(() => import('../pages/WishlistPage'));
const PaymentPage          = lazy(() => import('../pages/PaymentPage'));
const PaymentFailedPage    = lazy(() => import('../pages/PaymentFailedPage'));

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Auth routes — redirect to home, modal handles auth now */}
      <Route path="/login"           element={<Navigate to={ROUTES.HOME} replace />} />
      <Route path="/register"        element={<Navigate to={ROUTES.HOME} replace />} />
      <Route path="/forgot-password" element={<Navigate to={ROUTES.HOME} replace />} />
      <Route path="/reset-password"  element={<Navigate to={ROUTES.HOME} replace />} />
      {/* verify-otp kept for direct email link flows */}
      <Route path="/verify-otp"      element={<Navigate to={ROUTES.HOME} replace />} />

      {/* Public store routes — no sidebar */}
      <Route path="/"                element={<Layout public hero={<HomeBannerSlider />}><HomePage /></Layout>} />
      <Route path="/products"        element={<Layout public><ProductsPage /></Layout>} />
      <Route path="/products/:slug"  element={<Layout public><ProductDetailPage /></Layout>} />
      <Route path="/cart"            element={<Layout public><CartPage /></Layout>} />
      <Route path="/checkout"        element={<ProtectedRoute><Layout public><CheckoutPage /></Layout></ProtectedRoute>} />
      <Route path="/order-success/:id" element={<ProtectedRoute><Layout public><OrderSuccessPage /></Layout></ProtectedRoute>} />
      <Route path="/orders"            element={<ProtectedRoute><Layout public><OrdersPage /></Layout></ProtectedRoute>} />
      <Route path="/orders/:id"        element={<ProtectedRoute><Layout public><OrderDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/payment/:orderId"  element={<ProtectedRoute><Layout public><PaymentPage /></Layout></ProtectedRoute>} />
      <Route path="/payment-failed"    element={<Layout public><PaymentFailedPage /></Layout>} />

      {/* Protected dashboard routes — with sidebar */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/profile"   element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/users"     element={<ProtectedRoute roles={['admin','super_admin']}><Layout><UsersPage /></Layout></ProtectedRoute>} />

      {/* Seller routes */}
      {/* Seller routes — dedicated layout */}
      <Route path="/seller" element={<ProtectedRoute roles={['seller','admin','super_admin']}><SellerLayout /></ProtectedRoute>}>
        <Route index                element={<SellerDashboardPage />} />
        <Route path="products"      element={<SellerProductsPage />} />
        <Route path="orders"        element={<SellerOrdersPage />} />
        <Route path="earnings"      element={<SellerEarningsPage />} />
        <Route path="store"         element={<SellerStorePage />} />
      </Route>
      <Route path="/store/:id"       element={<Layout public><StoreProfilePage /></Layout>} />
      <Route path="/become-seller"   element={<Layout public><BecomeSellerPage /></Layout>} />
      <Route path="/seller-guide"    element={<Layout public hero={<HeroBannerSlider />}><SellerGuidePage /></Layout>} />
      <Route path="/help"            element={<Layout public><HelpCenterPage /></Layout>} />
      <Route path="/help/seller"     element={<Layout public><SellerHelpPage /></Layout>} />
      <Route path="/faq"             element={<Layout public><FAQPage /></Layout>} />
      <Route path="/faq/:articleId"  element={<Layout public><FAQDetailPage /></Layout>} />
      <Route path="/wishlist"        element={<Layout public><WishlistPage /></Layout>} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin', 'super_admin']}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="users"                element={<AdminUsersPage />} />
        <Route path="orders"               element={<AdminOrdersPage />} />
        <Route path="products"             element={<AdminProductsPage />} />
        <Route path="categories"           element={<AdminCategoriesPage />} />
        <Route path="seller-applications"     element={<AdminSellerApplicationsPage />} />
        <Route path="seller-applications/:id" element={<SellerApplicationDetailPage />} />
        <Route path="coupons"              element={<AdminCouponsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
